import { useEffect } from "react"
import PropTypes from "prop-types"
import { Modal, Spin } from "antd"
import { useDispatch, useSelector } from "react-redux"
import ClientContactForm from "pagesV2/pro/clients/ClientsContactWindow"
import { fetchCommunicationTemplates, selectCommunicationTemplates } from "store/communicationTemplates"

const TITLE_BY_TYPE = { email: "Send Email", text: "Send Text", message: "Send Message" }

// Thin wrapper that reuses the existing client messaging form (ClientContactForm) outside
// the Clients screen — Bookings (SendMessageButton) and AI Assistant email links. Recipients are rows carrying { userId, user }; the form sends by numeric
// user_id, so rows without one are dropped. Default type "message" renders a body + send.
const SendClientMessageModal = ({ open, onClose, recipients, type, setSelectedRowKeys, redirectOnClose }) => {
  const dispatch = useDispatch()
  const { loading } = useSelector((store) => store.clients.sendMessage)

  // Templates aren't embedded in any booking payload, so pull them from the guide-level endpoint.
  // Fetched when the modal opens rather than on mount — the button renders on every table row.
  const templates = useSelector(selectCommunicationTemplates(type))
  const isTemplatesLoaded = useSelector((store) => store.communicationTemplates.loaded)
  useEffect(() => {
    if (open && !isTemplatesLoaded) dispatch(fetchCommunicationTemplates())
  }, [open, isTemplatesLoaded, dispatch])

  // Multiple selected reservations can belong to the same person, so collapse recipients by
  // user_id — the message is sent once per client and the UI (count + chips) must show one
  // entry per unique client, not one per booking.
  const clients = recipients
    .filter((recipient) => recipient.userId != null)
    .reduce((unique, recipient) => {
      const userId = Number(recipient.userId)
      if (!unique.some((client) => client.user_id === userId)) {
        unique.push({ user_id: userId, user: recipient.user || null })
      }
      return unique
    }, [])
  const userIds = clients.map((client) => client.user_id)

  return (
    <Modal title={TITLE_BY_TYPE[type] || "Send Message"} width={700} open={open} footer={null} onCancel={onClose} destroyOnClose>
      <Spin spinning={loading}>
        <ClientContactForm
          handleClose={onClose}
          type={type}
          clients={clients}
          userIds={userIds}
          setSelectedRowKeys={setSelectedRowKeys}
          templates={templates}
          redirectOnClose={redirectOnClose}
        />
      </Spin>
    </Modal>
  )
}

SendClientMessageModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  recipients: PropTypes.arrayOf(
    PropTypes.shape({
      userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      user: PropTypes.object
    })
  ),
  type: PropTypes.oneOf(["email", "text", "message"]),
  setSelectedRowKeys: PropTypes.func,
  redirectOnClose: PropTypes.bool
}

SendClientMessageModal.defaultProps = {
  recipients: [],
  type: "message",
  setSelectedRowKeys: undefined,
  redirectOnClose: true
}

export default SendClientMessageModal
